import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import SearchableSelect from '../../../components/ui/SearchableSelect';
import useTranslation from '../../../hooks/useTranslation';

// Define year pills as a constant — extend this array to add future years without touching JSX
const YEAR_PILLS = [2025, 2026, 2027];

const ProjectDetailFilterBar = ({ onFilterChange, activeFilters, projectCount = 0, clients = [] }) => {
  const { t } = useTranslation();
  const [universalSearch, setUniversalSearch] = useState(activeFilters?.universalSearch || '');
  const [clientFilter, setClientFilter] = useState(activeFilters?.clientFilter || '');
  const [yearFilter, setYearFilter] = useState(activeFilters?.yearFilter || null);

  // Sync if parent resets filters externally
  useEffect(() => {
    setUniversalSearch(activeFilters?.universalSearch || '');
    setClientFilter(activeFilters?.clientFilter || '');
    setYearFilter(activeFilters?.yearFilter || null);
  }, [activeFilters?.universalSearch, activeFilters?.clientFilter, activeFilters?.yearFilter]);

  const handleSearchChange = (value) => {
    setUniversalSearch(value);
    onFilterChange({ universalSearch: value, clientFilter, yearFilter });
  };

  const handleClientChange = (value) => {
    setClientFilter(value);
    onFilterChange({ universalSearch, clientFilter: value, yearFilter });
  };

  const handleYearChange = (year) => {
    const newYear = yearFilter === year ? null : year;
    setYearFilter(newYear);
    onFilterChange({ universalSearch, clientFilter, yearFilter: newYear });
  };

  const handleClearFilters = () => {
    setUniversalSearch('');
    setClientFilter('');
    setYearFilter(null);
    onFilterChange({ universalSearch: '', clientFilter: '', yearFilter: null });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      {/* Year Filter Pills — segmented container matching Répertoire's category pills */}
      <div className="flex items-center h-8 bg-white rounded-lg border border-gray-200 px-1 shadow-sm flex-shrink-0">
        {/* "Toutes/All" pill */}
        <button
          onClick={() => handleYearChange(null)}
          className={`h-6 px-3 text-xs font-medium rounded-md transition-all duration-200 ${
            yearFilter === null
              ? 'bg-primary text-white shadow-sm'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          {t('projectList.filter.yearFilter.all')}
        </button>
        {YEAR_PILLS?.map((year) => (
          <button
            key={year}
            onClick={() => handleYearChange(year)}
            className={`h-6 px-3 text-xs font-medium rounded-md transition-all duration-200 ${
              yearFilter === year
                ? 'bg-primary text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            {year}
          </button>
        ))}
      </div>
      {/* Search Input */}
      <input
        type="search"
        placeholder={t('projectList.filter.searchUniversal')}
        value={universalSearch}
        onChange={(e) => handleSearchChange(e?.target?.value)}
        className="h-8 flex-1 min-w-[220px] max-w-xs rounded-lg border border-gray-200 bg-white px-3 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors shadow-sm"
      />
      {/* Clients Dropdown */}
      {clients?.length > 0 && (
        <div className="flex-shrink-0 min-w-[160px]">
          <SearchableSelect
            options={[
              { value: '', label: t('projectList.filter.allClients') },
              ...clients?.map((client) => ({ value: client, label: client })),
            ]}
            value={clientFilter}
            onChange={(val) => handleClientChange(val)}
            placeholder={t('projectList.filter.allClients')}
            searchable={false}
          />
        </div>
      )}
      {/* Clear Filters Button */}
      <button
        onClick={handleClearFilters}
        className="h-8 inline-flex items-center gap-1.5 px-2.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
      >
        <Icon name="X" size={13} />
        {t('projectList.filter.clear')}
      </button>
      {/* Project Count */}
      <span className="text-xs text-gray-400 tabular-nums flex-shrink-0">
        {projectCount} {t('projectList.filter.projectsCount')}
      </span>
    </div>
  );
};

export default ProjectDetailFilterBar;